import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';
import { ORDER_TYPE, monthKey, startOfDay, periodMonthCount } from '@/services/directOrderService';

// Direct orders (assortment orders + returns) for the "Suivi de commande directe" screen.
//
// The legacy app already syncs exactly this dataset under ORDERSFULL — `POST Sales/Orders/Search`
// with `orderTypes: [7, 8]` (eagle.js, getOrdersFull). So the primary read is the legacy store, not
// the network: it is free, already offline, and identical in shape.
//
// Two limits of that sync drive the hybrid strategy below:
//  - it is capped at 1000 orders with no guaranteed sort, so a busy rep's history can be truncated
//    at an unknown date rather than at the 12-month boundary the spec assumes;
//  - it is skipped entirely inside the Back-Office iframe (`externis.hiddenApp`).
// Both are detected as "insufficient coverage" and, when online, completed by a scoped network call
// over the requested period only. Offline, whatever is cached is returned with `truncated: true` so
// the screen can say so instead of quietly under-reporting (CL-01).

// Mirrors `filterLines.limit` in eagle.js getOrdersFull — the cap that makes legacy coverage
// unknowable by date alone.
export const LEGACY_ORDER_LIMIT = 1000;

// Ceiling on a network top-up. Past it the screen asks the user to narrow the period instead of
// pulling and aggregating an unbounded history on a field device. One order more than the ceiling is
// requested, so an overflow can be told apart from a result that merely reaches it.
export const MAX_NETWORK_ORDERS = 2000;

// Offline history is 12 rolling months (spec §12 / CL-01): a longer period is not exploitable
// offline and the screen has to say so rather than render a partial total as if it were complete.
export const MAX_OFFLINE_MONTHS = 12;

// Returned when there is nothing at all to show — offline on a first-ever launch, or inside the BO
// iframe where ORDERSFULL is never synced. A neutral empty state, not placeholder data: it can be
// shown to real users.
const EMPTY_RESULT = { orders: [], coverage: { source: 'none', oldest: null, count: 0, truncated: true, exceedsOfflineWindow: false } };

const LEGACY_MEMORY_KEY = 'legacyOrdersFull';

const ordersCache = new CacheManager('VueCache', 'cache', {
	maxAge: 12 * 60 * 60 * 1000,
	memoryCacheTTL: 5 * 60 * 1000,
	logCategory: 'DirectOrder',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.directOrders.`,
});

function toTime(value) {
	if (value == null) return NaN;
	if (value instanceof Date) return value.getTime();
	return new Date(value).getTime();
}

function oldestCreatedDate(orders) {
	let oldest = null;
	for (const order of orders) {
		const time = toTime(order?.createdDate);
		if (Number.isNaN(time)) continue;
		if (oldest === null || time < oldest) oldest = time;
	}
	return oldest === null ? null : new Date(oldest);
}

function dayKey(date) {
	const d = new Date(date);
	return `${monthKey(d)}-${String(d.getDate()).padStart(2, '0')}`;
}

// Coverage is deliberately pessimistic on the 1000-order cap: at the cap we cannot tell whether the
// oldest order we hold is genuinely the oldest one or merely the oldest the server chose to return,
// so the window is reported as truncated even when its dates look complete.
export function evaluateCoverage(orders, startDate, limit = LEGACY_ORDER_LIMIT) {
	const count = orders.length;
	const oldest = oldestCreatedDate(orders);
	const atLimit = count >= limit;
	const startsBeforeOldest = startDate != null && oldest !== null && startOfDay(startDate).getTime() < oldest.getTime();

	return { count, oldest, truncated: atLimit || startsBeforeOldest || (count === 0 && startDate != null), atLimit, startsBeforeOldest };
}

async function readLegacyOrders() {
	const cached = ordersCache.getFromMemory(LEGACY_MEMORY_KEY);
	if (cached) return cached;

	try {
		const data = await iframeDataBridge.getStorageAsync('ORDERSFULL');
		const orders = Array.isArray(data) ? data : [];
		// Not persisted to our own IndexedDB: ORDERSFULL already lives there under the legacy store
		// and can run to a thousand orders with all their lines. Duplicating it would double the
		// storage cost and add a second copy that can go stale against the first.
		ordersCache.setInMemory(LEGACY_MEMORY_KEY, orders);
		log('info', 'DirectOrder', `📦 Read ${orders.length} orders from legacy storage (ORDERSFULL)`);
		return orders;
	} catch (error) {
		log('warn', 'DirectOrder', `⚠️ Legacy ORDERSFULL read failed: ${error.message}`);
		return [];
	}
}

// Rebuilds the legacy `ordersFilterModel` payload, including the own-delivery-locations narrowing
// that svc.js applies on its own behalf (svc.sales.orders.getData) — the endpoint is shared, so
// skipping it here would widen the perimeter beyond what the legacy app itself is allowed to see.
//
// The server's status date is the order's modifiedDate (vwOrdersTracking.js maps its "Modified date"
// filter onto statusDateMin/Max). An order is never modified before it is created, so a lower bound
// on it keeps every order created in the period. No upper bound is sent: it would drop the orders
// created in the period but modified after it ended (a later ERP status change). filterOrders
// applies the exact createdDate bounds client-side.
function buildSearchFilter(startDate) {
	const filter = {
		start: 0,
		limit: MAX_NETWORK_ORDERS + 1,
		orderTypes: [ORDER_TYPE.REGULAR, ORDER_TYPE.RETURN],
		statusDateMin: startDate ? startOfDay(startDate).toISOString() : undefined,
		organisations: [],
		deliveryLocationIds: [],
		status: [],
	};

	// Read straight off the iframe window rather than through getExternis()/getEcosVM(): those two
	// deep-clone the entire legacy state to make it Vue-reactive, and all that is needed here is a
	// boolean and a list of ids.
	const externis = iframeDataBridge.getIframeWindow()?.externis;
	if (externis?.filterByOwnDeliveryLocations) {
		filter.deliveryLocationIds = (externis.ecosVM?.ownDeliveryLocations ?? []).map((dl) => dl.id);
	}

	return filter;
}

async function fetchOrdersFromAPI(startDate) {
	const response = await authenticatedFetch(getApiUrl('Sales/Orders/Search'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(buildSearchFilter(startDate)) }, 'tokenSales');

	if (!response.ok) throw new Error(`HTTP ${response.status}`);

	const orders = await response.json();
	log('success', 'DirectOrder', `✅ Fetched ${orders?.length ?? 0} direct orders from ${startDate ? startOfDay(startDate).toISOString().slice(0, 10) : 'origin'}`);
	return Array.isArray(orders) ? orders : [];
}

// Not fetchOrCache: it turns any failure into its fallback, and an oversize result has to reach the
// screen as such. An oversize result is not cached either, so the next visit re-checks the server
// instead of replaying it from IndexedDB.
async function readOrFetchExtendedOrders(key, startDate, forceRefresh) {
	if (!forceRefresh) {
		const cached = ordersCache.getFromMemory(key) ?? (await ordersCache.get(key).catch(() => null));
		if (Array.isArray(cached)) {
			ordersCache.setInMemory(key, cached);
			return { orders: cached, tooLarge: false };
		}
	}

	const orders = await fetchOrdersFromAPI(startDate);
	if (orders.length > MAX_NETWORK_ORDERS) {
		log('warn', 'DirectOrder', `⚠️ ${orders.length} orders from ${startDate ? dayKey(startDate) : 'origin'} exceed the ${MAX_NETWORK_ORDERS} ceiling`);
		return { orders: [], tooLarge: true };
	}

	await ordersCache.put(key, orders);
	ordersCache.setInMemory(key, orders);
	return { orders, tooLarge: false };
}

/**
 * Orders covering the requested period, plus what is known about how complete that coverage is.
 * `coverage.tooLarge` means the period needs more than MAX_NETWORK_ORDERS orders from the server:
 * nothing is returned and the caller must ask for a narrower period.
 *
 * @returns {Promise<{orders: Array, coverage: {source: string, oldest: Date|null, count: number, truncated: boolean, exceedsOfflineWindow: boolean, tooLarge?: boolean}}>}
 */
export async function getDirectOrders({ startDate = null, endDate = null, forceRefresh = false } = {}) {
	const online = iframeDataBridge.isOnline();
	const exceedsOfflineWindow = startDate && endDate ? periodMonthCount(startDate, endDate) > MAX_OFFLINE_MONTHS : false;

	const legacyOrders = forceRefresh ? [] : await readLegacyOrders();
	const legacyCoverage = evaluateCoverage(legacyOrders, startDate);

	if (!forceRefresh && !legacyCoverage.truncated) {
		return { orders: legacyOrders, coverage: { source: 'legacy', ...legacyCoverage, exceedsOfflineWindow } };
	}

	// Coverage is short. A previous network top-up from this same start day may already be cached —
	// prefer it over another round trip, and it is also the only thing left to fall back on offline.
	// Keyed by day, not month: a top-up from the 24th lacks the orders of the 1st to the 23rd. The
	// end date plays no part since the top-up is never bounded above (see buildSearchFilter).
	const extendedKey = `extended.${startDate ? dayKey(startDate) : 'all'}`;

	if (!online) {
		const cached = await ordersCache.get(extendedKey).catch(() => null);
		if (Array.isArray(cached) && cached.length > 0) {
			return { orders: cached, coverage: { source: 'cache', ...evaluateCoverage(cached, startDate, MAX_NETWORK_ORDERS + 1), exceedsOfflineWindow } };
		}
		if (legacyOrders.length > 0) {
			log('warn', 'DirectOrder', '⚠️ Offline with truncated history — returning legacy orders flagged as incomplete');
			return { orders: legacyOrders, coverage: { source: 'legacy', ...legacyCoverage, truncated: true, exceedsOfflineWindow } };
		}
		return { ...EMPTY_RESULT, coverage: { ...EMPTY_RESULT.coverage, exceedsOfflineWindow } };
	}

	try {
		const { orders, tooLarge } = await readOrFetchExtendedOrders(extendedKey, startDate, forceRefresh);
		if (tooLarge) return { orders: [], coverage: { ...EMPTY_RESULT.coverage, source: 'network', tooLarge: true, exceedsOfflineWindow } };
		return { orders, coverage: { source: 'network', ...evaluateCoverage(orders, startDate, MAX_NETWORK_ORDERS + 1), exceedsOfflineWindow } };
	} catch (error) {
		log('warn', 'DirectOrder', `⚠️ Network top-up failed, falling back to legacy history: ${error.message}`);
	}

	if (legacyOrders.length > 0) {
		return { orders: legacyOrders, coverage: { source: 'legacy', ...legacyCoverage, truncated: true, exceedsOfflineWindow } };
	}
	return { ...EMPTY_RESULT, coverage: { ...EMPTY_RESULT.coverage, exceedsOfflineWindow } };
}

// Warms the in-memory copy of ORDERSFULL during the background sync pass so the screen and the home
// widget open instantly. Deliberately never hits the network: it runs on every sync, and a periodic
// unsolicited Sales/Orders/Search over the whole history is exactly the kind of cost that has
// already been traced as a sync slowdown on this app.
export async function primeDirectOrders() {
	const orders = await readLegacyOrders();
	return orders.length;
}

export async function refreshDirectOrders() {
	ordersCache.clearMemory();
	await ordersCache.clear();
}
